import * as React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';

import { Button, ButtonContainer, Typography } from '@emburse/ui-core';

import { BulkUploadWizardError } from '@ReactComponents/BulkUploadWizardError/BulkUploadWizardError';
import { BulkUploadWizardErrorDetails } from '@ReactComponents/BulkUploadWizardErrorDetails';
import { BulkUploadWizardWizardPanelKeys } from '@ReactComponents/BulkUploadWizardModal';
// Components
import { Dropzone } from '@ReactComponents/Dropzone';
import { Spinner } from '@ReactComponents/Spinner';
import { IWizardPanelProps, WizardPanel } from '@ReactComponents/Wizard/Wizard';

import {
  ClearProcessingFailure,
  ReceiveBulkUploadDiffs,
  ReceiveCardStatementUploadDiffs,
  RecordProcessingFailure,
  SaveCsvData,
  ToggleViewingErrorDetails
} from '@Actions/bulk_upload';
import { IAppStateContainer } from '@Reducers/index';

import { BulkUploadApiService as bulkUploadApiService } from '@ClientApi/bulkUpload';
import { BulkUploadType, IDiff, IUpdateDiff } from '@Shared/bulk_upload';
import { formatDate } from '@Shared/helpers/abacusTimeFormatter';
import { ILinkedAccountModel } from '@Shared/models';
import { IExpenseTagBucketModel } from '@Shared/models/ExpenseTagBucket';

interface IBulkUploadWizardStartProps {
  clearError: () => void;
  receiveDiffs: (diffs: IDiff[]) => void;
  recordProcessingFailure: (error: string, errorDiffs: IUpdateDiff[]) => void;
  toggleViewingErrorDetails: () => void;
  onSaveCsvData: (data: string, fileType: string) => void;
  tagBuckets?: IExpenseTagBucketModel[];
  categoryFilterBucket?: IExpenseTagBucketModel;
  error?: string;
  errorDiffs?: IUpdateDiff[];
  viewingErrorDetails: boolean;
  uploadType: BulkUploadType;
  isManualSync: boolean;
  cardProgram?: ILinkedAccountModel; // only for uploadType === 'card_statement'
  bucket?: IExpenseTagBucketModel; // only for uploadType === 'expense_tags'
}

interface IBulkUploadWizardStartState {
  isDragging?: boolean;
  isUploading?: boolean;
}

function renderProgramIdentifierWithPriorUpload(account: ILinkedAccountModel) {
  const lastUpdateString = account.last_successful_update_at && formatDate(account.last_successful_update_at, 'PPP pp');
  return (
    <div className="card-statement-program-info">
      <strong>{account.name}</strong>
      {lastUpdateString && <span> Last Upload {lastUpdateString}</span>}
      <hr />
    </div>
  );
}

function renderEmbarkLoadingPanel() {
  return (
    <div style={{ minHeight: '100px' }}>
      <Spinner small centered loadingText="Processing File" />
    </div>
  );
}

export class BulkUploadWizardStart extends WizardPanel<
  BulkUploadWizardWizardPanelKeys,
  IBulkUploadWizardStartProps,
  IBulkUploadWizardStartState
> {
  constructor(props: IWizardPanelProps<BulkUploadWizardWizardPanelKeys> & IBulkUploadWizardStartProps) {
    super(props);

    this.state = {
      isDragging: false
    };
  }

  getInstructionsText() {
    const { uploadType, bucket } = this.props;

    if (uploadType === 'team') {
      return `Update your team's ${window.whitelabelConfiguration.tags.brandName} account with the most recent record of your employees' information.`;
    } else if (uploadType === 'categories') {
      return `Update your team's ${window.whitelabelConfiguration.tags.brandName} account with your current set of categories.`;
    } else if (uploadType === 'card_statement') {
      return (
        <>
          Upload a statement from your card provider.
          {window.whitelabelConfiguration.tags.brandName} will identify new card holders from your file, as well as any potential
          duplicates.
        </>
      );
    } else if (uploadType === 'expense_tags') {
      const bucketType = bucket.bucket_type === 'expense_field' ? 'Expense Field' : 'Team Field';
      return `Update ${bucketType} "${bucket.name}" with your current set of items.`;
    }
  }

  onDrop(acceptedFiles: File[]) {
    const me = this;
    const uploadType = this.props.uploadType;

    const account = this.props.cardProgram;

    const bucketId = this.props.bucket?.id;

    this.setState({ isDragging: false, isUploading: true });

    if (acceptedFiles.length) {
      const file = acceptedFiles[0];

      const r = new FileReader();

      r.onloadend = function (e) {
        const data = e.target.result as string;
        const fileNameSplitOnPeriods = file.name.split('.');
        const fileType = fileNameSplitOnPeriods[fileNameSplitOnPeriods.length - 1].toLowerCase();
        const accountId = (account && account.id) || null;

        bulkUploadApiService.uploadFile(uploadType, data, fileType, accountId, bucketId).then((responseObj: any) => {
          if (!responseObj.valid) {
            me.props.recordProcessingFailure(responseObj.message, responseObj.diffs);
            me.setState({ isUploading: false });
          } else {
            me.props.onSaveCsvData(data, fileType);
            me.props.receiveDiffs(responseObj.diffs);
            me.props.changePanel('summary');
          }
        });
      };

      r.readAsText(file);
    }
  }

  onDragLeave() {
    this.toggleDrag(false);
  }

  onDragEnter() {
    this.toggleDrag(true);
  }

  toggleDrag(isDragging: boolean = false) {
    if (this.state.isDragging === isDragging) {
      return;
    }

    this.setState({ isDragging });
  }

  onDownloadTemplate() {
    const { uploadType, categoryFilterBucket, tagBuckets, isManualSync } = this.props;

    if (uploadType === 'team') {
      let tagBucketNames = '';
      if (tagBuckets && tagBuckets.length) {
        tagBucketNames = ',' + tagBuckets.map((bucket) => bucket.name);
      }
      let csvContent = 'Email,Role,Manager Email';
      if (window.ORG.is_manager_card_issuing_enabled) {
        csvContent += ',Manager Card Issuing Limit, Is Physical Card Issuing Enabled';
      }
      if (window.FEATURES.show_employee_ids) {
        csvContent += ',Employee ID';
      }
      if (window.FEATURES.travel) {
        csvContent += ',Travel Enabled';
      }
      csvContent += tagBucketNames;
      const encodedUri = encodeURI(csvContent);
      return encodedUri;
    }
    if (uploadType === 'categories') {
      let csvContent = 'Category,Subcategory';
      // Add Visible to: xxxxx if a team field is scoped
      if (categoryFilterBucket) {
        csvContent += `,Visible to: ${categoryFilterBucket.name}`;
      }
      if (isManualSync) {
        csvContent += ',Chart of Accounts';
      }
      const encodedUri = encodeURI(csvContent);
      return encodedUri;
    }
    if (uploadType === 'card_statement') {
      const csvContent = 'Transaction Date,Posted Date,Card No.,Merchant Name,Category,Debit,Credit';
      const encodedUri = encodeURI(csvContent);
      return encodedUri;
    }
    if (uploadType === 'expense_tags') {
      const csvContent = ['Name', 'Description'].join(',');
      const encodedUri = encodeURI(csvContent);
      return encodedUri;
    }
  }

  renderEmbarkStartPanel(uploadType: BulkUploadType) {
    if (this.state.isUploading) {
      return renderEmbarkLoadingPanel();
    }

    const instructionsText = this.getInstructionsText();

    const teamBulkUploadDocumentationLink = (
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginRight: 30 }}
        href={window.whitelabelConfiguration.supportLinks.bulkUpdateTeam}
      >
        About format
      </a>
    );

    const categoryBulkUploadDocumentationLink = (
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginRight: 30 }}
        href={window.whitelabelConfiguration.supportLinks.bulkUpdateCategories}
      >
        About format
      </a>
    );

    const statementUploadDocumentationLink = (
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginRight: 30 }}
        href={window.whitelabelConfiguration.supportLinks.manualStatementUpload}
      >
        About format
      </a>
    );

    const customBodyText = uploadType === 'card_statement' ? 'Drag a file here' : null;

    return (
      <div>
        {uploadType === 'card_statement' && renderProgramIdentifierWithPriorUpload(this.props.cardProgram)}
        <Typography>{instructionsText}</Typography>
        {uploadType !== 'card_statement' && (
          <Typography>
            Your upload must be a properly formatted <em>.csv</em> file.
          </Typography>
        )}
        {uploadType === 'card_statement' && (
          <Typography>
            Your upload must be a properly formatted <em>.csv</em> or <em>.ofx</em> file.
          </Typography>
        )}
        {uploadType === 'team' && teamBulkUploadDocumentationLink}
        {uploadType === 'categories' && categoryBulkUploadDocumentationLink}
        {uploadType === 'card_statement' && statementUploadDocumentationLink}
        <a href={`data:text/csv,${this.onDownloadTemplate()}`} download="Bulk Upload Template.csv">
          Download template
        </a>
        <Dropzone
          customBodyText={customBodyText}
          isDragging={this.state.isDragging}
          multiple={false}
          onDragEnter={() => this.onDragEnter()}
          onDragLeave={() => this.onDragLeave()}
          onDrop={(acceptedFiles) => this.onDrop(acceptedFiles)}
        />
      </div>
    );
  }

  renderEmbarkErrorSummary(errorType: 'INLINE' | 'FILE') {
    return (
      <div>
        <BulkUploadWizardError errorDiffs={this.props.errorDiffs} errorType={errorType} errorMessage={this.props.error} />
        <ButtonContainer alignButtons="right" style={{ marginTop: 20 }}>
          <Button color="secondary" onClick={() => this.props.clearError()}>
            Back
          </Button>
          {errorType === 'INLINE' && <Button onClick={() => this.props.toggleViewingErrorDetails()}>Review Errors</Button>}
        </ButtonContainer>
      </div>
    );
  }

  renderEmbarkErrorDetails() {
    return (
      <div>
        <BulkUploadWizardErrorDetails uploadType={this.props.uploadType} errorDiffs={this.props.errorDiffs} />
        <ButtonContainer alignButtons="right" style={{ marginTop: 20 }}>
          <Button onClick={() => this.props.toggleViewingErrorDetails()}>Back</Button>
        </ButtonContainer>
      </div>
    );
  }

  renderEmbarkErrorContent(props: IBulkUploadWizardStartProps) {
    const errorType = props.errorDiffs.length ? 'INLINE' : 'FILE';
    return props.viewingErrorDetails ? this.renderEmbarkErrorDetails() : this.renderEmbarkErrorSummary(errorType);
  }

  render() {
    return <div>{this.props.error ? this.renderEmbarkErrorContent(this.props) : this.renderEmbarkStartPanel(this.props.uploadType)}</div>;
  }
}

// Connected Component

const mapStateToProps = (state: IAppStateContainer, parentProps: IWizardPanelProps<BulkUploadWizardWizardPanelKeys>) => {
  const s = state.bulkUpload;

  return {
    ...parentProps,
    error: s.error,
    errorDiffs: s.errorDiffs,
    viewingErrorDetails: s.viewingErrorDetails
  };
};

const mapDispatchToProps = (dispatch: (action: AnyAction) => void) => ({
  clearError: () => {
    dispatch(new ClearProcessingFailure());
  },
  receiveDiffs: (diffs: IDiff[]) => {
    dispatch(new ReceiveBulkUploadDiffs(diffs));
  },
  recordProcessingFailure: (error: string, errorDiffs: IUpdateDiff[]) => {
    dispatch(new RecordProcessingFailure(error, errorDiffs));
  },
  toggleViewingErrorDetails: () => {
    dispatch(new ToggleViewingErrorDetails());
  },
  onSaveCsvData: (data: string) => {
    dispatch(new SaveCsvData(data, 'csv'));
  }
});

// Card Statement Upload connections

const cardStatementStateToProps = (state: IAppStateContainer, parentProps: IWizardPanelProps<BulkUploadWizardWizardPanelKeys>) => {
  const s = state.bulkUpload;
  const company = state.company;
  return {
    ...parentProps,
    error: s.error,
    errorDiffs: s.errorDiffs,
    viewingErrorDetails: s.viewingErrorDetails,
    isManualSync: company.accountingIntegration && company.accountingIntegration.is_manual
  };
};

const cardStatementDispatchToProps = (dispatch: (action: AnyAction) => void) => ({
  clearError: () => {
    dispatch(new ClearProcessingFailure());
  },
  receiveDiffs: (diffs: IDiff[]) => {
    dispatch(new ReceiveCardStatementUploadDiffs(diffs));
  },
  recordProcessingFailure: (error: string, errorDiffs: IUpdateDiff[]) => {
    dispatch(new RecordProcessingFailure(error, errorDiffs));
  },
  toggleViewingErrorDetails: () => {
    dispatch(new ToggleViewingErrorDetails());
  },
  onSaveCsvData: (data: string, fileType: string) => {
    dispatch(new SaveCsvData(data, fileType));
  }
});

export const ConnectedBulkUploadWizardStart = connect(mapStateToProps, mapDispatchToProps)(BulkUploadWizardStart);

export const CardStatementUploadWizardStart = connect(cardStatementStateToProps, cardStatementDispatchToProps)(BulkUploadWizardStart);
